import {
  atBuffer2DIndex,
  GeckoBinnedDataBuffer,
  useCdfRawValues,
} from 'features/dataLayers'
import { SelectedBins, selectionToBins } from 'features/fde/binningUtils'
import {
  IndicationClassification,
  IndicationClassificationInfo,
  IndicationItem,
} from 'features/indicationTriage/api/indicationTriageApi'
import mapValues from 'lodash/mapValues'
import sortBy from 'lodash/sortBy'
import sortedIndex from 'lodash/sortedIndex'
import { createContext, useMemo } from 'react'
import {
  GeckoComponentDataFrame,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoView,
} from 'types'

import {
  deriveIndicationColorState,
  IndicationColorState,
  UNKNOWN_COLOR_SCHEME,
} from './indicationClassificationColors'

type Ordering = 'asc' | 'desc' | 'source'
export type ClassificationDef = {
  threshold?: number
  color?: string
  description?: string
}
export type IndicationClassificationConfig = {
  id: string
  title?: string
  viewId?: string
  unitId?: string
  ordering?: Ordering
  showTags?: boolean
  classifications: Record<IndicationClassification, ClassificationDef>
}

export const DEFAULT_CLASSIFICATION_ORDERING: Ordering = 'desc'

type IndicationRegion = {
  id: string
  bins: SelectedBins
}

export const DEFAULT_CLASSIFICATION_CONFIG: IndicationClassificationConfig = {
  id: '@@DEFAULT@@',
  viewId: 'remaining-life',
  unitId: 'years',
  ordering: 'asc',
  classifications: {
    Alert: {
      threshold: 5,
      color: 'rgba(93, 88, 24, 1)',
    },
    Defect: {
      threshold: 0,
      color: 'rgba(138, 29, 34, 1)',
    },
    Pass: {
      threshold: 150,
      color: 'rgba(39, 128, 25, 1)',
    },
  },
}

export type IndicationClassificationConfigContextType = {
  config: IndicationClassificationConfig
  colors: Record<IndicationClassification, IndicationColorState>
}

export function createClassificationIndicationConfigContextValue(
  config: IndicationClassificationConfig
): IndicationClassificationConfigContextType {
  return {
    config: config,
    colors: mapValues(config.classifications, (conf) =>
      conf.color ? deriveIndicationColorState(conf.color) : UNKNOWN_COLOR_SCHEME
    ),
  }
}

export const IndicationClassificationConfigContext =
  createContext<IndicationClassificationConfigContextType>(
    createClassificationIndicationConfigContextValue(
      DEFAULT_CLASSIFICATION_CONFIG
    )
  )

/**
 * Returns a function which classifies an indication based on its
 * reference value.
 */
function getClassifier(
  config: Omit<IndicationClassificationConfig, 'viewId'>,
  dataUnit: GeckoMeasurementUnit,
  ordering: Ordering
): (value: number) => IndicationClassification {
  const configUnit = GeckoMeasurementUnit.GetByID(config.unitId)
  if (!configUnit) {
    throw Error(`Unknown unit: ${config.unitId}`)
  }
  const thresholdEntries = sortBy(
    Object.entries(config.classifications)
      .map(
        ([classification, conf]) =>
          [classification, conf.threshold] as [
            IndicationClassification,
            number | undefined,
          ]
      )
      .map(([classification, threshold]) => {
        return [
          classification,
          threshold !== undefined
            ? GeckoMeasurement.CreateFromValue(threshold, configUnit).convertTo(
                dataUnit
              ).num
            : undefined,
        ] as [IndicationClassification, number]
      }),
    ([, value]) => (ordering === 'asc' ? value : value * -1)
  )
  const thresholds = thresholdEntries.map(([, value]) => value)
  return (value: number) => {
    const index = sortedIndex(thresholds, value)
    return index >= 0 && index < thresholdEntries.length
      ? thresholdEntries[index][0]
      : thresholdEntries[thresholdEntries.length - 1][0]
  }
}

type IndicationClassificationResult = IndicationClassificationInfo & {
  id: string
}

function doClassification(
  regions: readonly IndicationRegion[],
  buffer: GeckoBinnedDataBuffer,
  dataUnit: GeckoMeasurementUnit,
  config: Omit<IndicationClassificationConfig, 'viewId'>
): IndicationClassificationResult[] {
  const classificationOrdering =
    config.ordering ?? DEFAULT_CLASSIFICATION_ORDERING
  // Create the classifier outside of the loop - its invariant.
  const classifier = getClassifier(config, dataUnit, classificationOrdering)
  return regions
    .map((region) => {
      const {
        minCorner: [minX, minY],
        maxCorner: [maxX, maxY],
      } = region.bins
      let refVal = NaN
      // Don't instantiate arrays or anything here - that'll slow it down a lot
      // and only keep track of the refVal
      for (let x = minX; x < maxX; x++) {
        for (let y = minY; y < maxY; y++) {
          const val = atBuffer2DIndex(buffer, x, y)
          if (val !== undefined && !isNaN(val)) {
            if (isNaN(refVal)) {
              refVal = val
            } else {
              refVal =
                classificationOrdering === 'asc'
                  ? Math.min(refVal, val)
                  : Math.max(refVal, val)
            }
          }
        }
      }
      return { id: region.id, refVal }
    })
    .filter(({ refVal }) => !isNaN(refVal))
    .map(({ id, refVal }) => {
      return {
        id,
        classification: classifier(refVal),
        refVal: GeckoMeasurement.CreateFromValue(refVal, dataUnit),
      }
    })
}

function findRelevantCdf(
  componentDataFrames: readonly GeckoComponentDataFrame[],
  underlyingCdf: GeckoComponentDataFrame,
  view: GeckoView
): GeckoComponentDataFrame | undefined {
  return componentDataFrames.find(({ data }) => {
    return (
      data.params.view === view &&
      data.binning.x_bin_count === underlyingCdf.data.binning.x_bin_count &&
      data.binning.y_bin_count === underlyingCdf.data.binning.y_bin_count &&
      data.binning.bin_size.height ===
        underlyingCdf.data.binning.bin_size.height &&
      data.binning.bin_size.width === underlyingCdf.data.binning.bin_size.width
    )
  })
}

/**
 * This is the main entrypoint.
 * Generate classifications for a list of indications based on the provided config.
 * @param indications the indications to classify
 * @param indicationClassificationConfig the config to use
 * @returns a Map<> of indication ID to its classification
 */
export function useClassifyIndications(
  indications: ReadonlyArray<IndicationItem>,
  indicationClassificationConfig: IndicationClassificationConfig | null,
  componentDataFrames: ReadonlyArray<GeckoComponentDataFrame>,
  _underlyingCDF: GeckoComponentDataFrame | undefined
): {
  classifications: ReadonlyMap<string, IndicationClassificationResult>
  isLoading: boolean
} {
  // This is to prevent use reloading all the classifications every time
  // we put a markup layer into the cache
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const underlyingCDF = useMemo(() => _underlyingCDF, [_underlyingCDF?.id])
  const _relevantCdf: GeckoComponentDataFrame | null = useMemo(() => {
    const view = indicationClassificationConfig?.viewId
      ? GeckoView.GetByID(indicationClassificationConfig.viewId)
      : null
    return underlyingCDF && view
      ? (findRelevantCdf(componentDataFrames, underlyingCDF, view) ?? null)
      : null
  }, [componentDataFrames, indicationClassificationConfig, underlyingCDF])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const relevantCdf = useMemo(() => _relevantCdf, [_relevantCdf?.id])
  const binnedDataBufferFetch = useCdfRawValues(relevantCdf ?? undefined)

  const indicationRegions = useMemo(() => {
    return indications
      .map((ind) => {
        return ind.selection && relevantCdf
          ? {
              id: ind.id,
              bins: selectionToBins(
                ind.selection,
                relevantCdf.data.binning,
                relevantCdf.data.geometry
              ),
            }
          : null
      })
      .filter((region) => !!region)
      .map((region) => region as IndicationRegion)
  }, [indications, relevantCdf])

  return useMemo(() => {
    const classificationTuples =
      binnedDataBufferFetch.buffer &&
      relevantCdf &&
      underlyingCDF &&
      indicationClassificationConfig &&
      indicationClassificationConfig.unitId &&
      indicationClassificationConfig.viewId
        ? doClassification(
            indicationRegions,
            binnedDataBufferFetch.buffer,
            relevantCdf.data.params.measurement_unit,
            indicationClassificationConfig
          )
        : []
    const map = new Map<string, IndicationClassificationResult>()
    classificationTuples.forEach((clsTup) => {
      if (clsTup.classification) {
        map.set(clsTup.id, clsTup)
      }
    })
    return { classifications: map, isLoading: binnedDataBufferFetch.isLoading }
  }, [
    binnedDataBufferFetch.buffer,
    binnedDataBufferFetch.isLoading,
    indicationClassificationConfig,
    indicationRegions,
    relevantCdf,
    underlyingCDF,
  ])
}
